import { dx, dy, type Direction } from '../../sim/direction';
import { TILE_SIZE } from '../layout';

export interface MowerPose {
  x: number;
  y: number;
  dir: Direction;
  progress: number;
  /** Backing up to the tile center (progress counts down). */
  backing?: boolean;
}

/**
 * World-pixel center of the mower for rendering, interpolated `alpha` of a tick ahead of the last
 * simulated state. When the mower sits at a tile center and will not advance next tick (blocked
 * ahead, or the attempt is over), it is drawn exactly at the center to avoid a visible overshoot.
 */
export function mowerPixelPosition(
  pose: MowerPose,
  ticksPerTile: number,
  alpha: number,
  willAdvance: boolean,
): { px: number; py: number } {
  const ticks = pose.backing
    ? Math.max(0, pose.progress - alpha)
    : pose.progress === 0 && !willAdvance
      ? 0
      : pose.progress + alpha;
  const frac = Math.min(ticks / ticksPerTile, 1);
  return {
    px: (pose.x + 0.5 + dx(pose.dir) * frac) * TILE_SIZE,
    py: (pose.y + 0.5 + dy(pose.dir) * frac) * TILE_SIZE,
  };
}

/**
 * The world point the camera centers on, along one axis. Small maps stay centered; larger ones
 * keep `focus` at `anchor` (0..1 of the view) while clamping to the map edges. Sizes are in world
 * pixels (the lawn camera is always 1x).
 */
export function cameraCenter(
  mapSize: number,
  viewSize: number,
  focus: number,
  anchor = 0.5,
): number {
  if (mapSize <= viewSize) return mapSize / 2;
  const desired = focus + viewSize * (0.5 - anchor);
  return Math.max(viewSize / 2, Math.min(mapSize - viewSize / 2, desired));
}
